import { createSelector } from 'reselect';

import { EMPLOYER_TYPE_AIRFORCE } from '../../constants/employerTypes';

import navModes from './nav_modes';

const GIFT_MESSAGES = {
  '((Sent Flowers))': require('../../../resources/gift-flowers.png'),
  '((Sent Love Vibes))': require('../../../resources/gift-love-vibes.png'),
  '((Sent a Teddy Bear))': require('../../../resources/gift-bear.png'),
  '((Sent Chocolates))': require('../../../resources/gift-chocolates.png'),
  '((Sent a Puppy))': require('../../../resources/puppy.png'),
};
const F16_TEXT = '((Sent an F16))';
const MQ9_TEXT = '((Sent an MQ9))';
const AIRFORCE_GIFT_MESSAGES = {
  [F16_TEXT]: require('../../../resources/f16.png'),
  [MQ9_TEXT]: require('../../../resources/mq9.png'),
};

const getCurrentCaseId = (state) => state.application.currentCaseId;
const getCurrentTopicId = (state) => state.application.currentTopicId;
const getCurrentEmployerTopicId = (state) => state.application.currentEmployerTopicId;
const getCurrentOnCallTopicId = (state) => state.application.currentOnCallTopicId;
const getTopics = (state) => state.entities.topics;
const getFollowers = (state) => state.entities.followers;
const getNavMode = (state) => state.screens.messaging.navMode;

import { getCurrentCaseParticipants } from '../cases/selector';

import { getCurrentEmployer, getAssociatedEmployer } from '../../selectors/employers';

// get topics associated with current case, with members property as a list of objects with
// participant and profile properties (case participant and profile from followers)
function _mapMembers(memberIds, participants, followers) {
  if (!(memberIds && memberIds.length)) return [];

  return memberIds.reduce((result, memberId) => {
    const memberParticipant = participants.find(
      (p) => p.participant?.userId === parseInt(memberId)
    );
    if (memberParticipant) result.push(memberParticipant);
    else if (followers[memberId]) result.push(followers[memberId]);
    return result;
  }, []);
}
export const getTopicsFromCurrentCase = createSelector(
  getTopics,
  getCurrentCaseId,
  getCurrentCaseParticipants,
  getFollowers,
  (topics, currentCaseId, participants, followers) => {
    if (!topics || !currentCaseId) return [];

    return Object.keys(topics)
      .map((k) => topics[k])
      .filter((t) => t.caseId === currentCaseId)
      .map((t) => ({
        ...t,
        members: _mapMembers(t.members, participants, followers),
      }));
  }
);
export const getTopicsFromCurrentEmployer = createSelector(
  getTopics,
  getCurrentEmployer,
  getFollowers,
  (topics, employer, followers) => {
    if (!topics || !employer) return [];

    return Object.keys(topics)
      .map((k) => topics[k])
      .filter((t) => t.employerId === employer.employerId)
      .map((t) => ({
        ...t,
        members: _mapMembers(t.members, [], followers),
      }));
  }
);

export const getOnCallTopics = createSelector(
  getTopicsFromCurrentEmployer,
  getCurrentEmployer,
  (employerTopics, employer) => {
    const medicalServices = employer?.uiConfig.medicalServices;
    if (!medicalServices?.length) return [];
    return Object.keys(employerTopics)
      .map((k) => employerTopics[k])
      .filter((t) => medicalServices.includes(t.name));
  }
);

export const getProviderTopics = createSelector(getTopicsFromCurrentEmployer, (employerTopics) => {
  if (!employerTopics) return [];
  return employerTopics.filter((t) => t.name === '_');
});

/**
 * Returns the current topic
 */
export const getCurrentTopic = createSelector(
  getTopicsFromCurrentCase,
  getTopicsFromCurrentEmployer,
  getCurrentTopicId,
  getCurrentEmployerTopicId,
  getCurrentOnCallTopicId,
  getNavMode,
  (
    caseTopics,
    employerTopics,
    currentTopicId,
    currentEmployerTopicId,
    currentOnCallTopicId,
    navMode
  ) => {
    if (navMode === navModes.CASE) {
      if (!currentTopicId) return null;
      return caseTopics.find((t) => t.topicId === currentTopicId);
    } else if (navMode === navModes.EMPLOYER) {
      if (!currentEmployerTopicId) return null;
      return employerTopics.find((t) => t.topicId === currentEmployerTopicId);
    } else {
      if (!currentOnCallTopicId) return null;
      return employerTopics.find((t) => t.topicId === currentOnCallTopicId);
    }
  }
);

export const getGiftTypes = createSelector(getAssociatedEmployer, (employer) => {
  const isAirforce = employer?.uiConfig?.type === EMPLOYER_TYPE_AIRFORCE;
  return {
    ...(!isAirforce ? GIFT_MESSAGES : {}),
    ...(isAirforce ? AIRFORCE_GIFT_MESSAGES : {}),
  };
});
function uniqueKeyForMessage(message) {
  return `${message.messageId}`;
}
function _isImageFile(fileName) {
  return fileName && !_isVideoFile(fileName);
}
function _isVideoFile(fileName) {
  return fileName && !!(fileName.endsWith('.mov') || fileName.endsWith('.mp4'));
}
function _messagesWithMetaData(messages, metaData) {
  return Object.keys(messages).map((id) => {
    return {
      ...messages[id],
      ...metaData,
    };
  });
}

const getMessagesFromCurrentTopic = createSelector(getCurrentTopic, (topic) => {
  if (!topic) return null;
  return _messagesWithMetaData(topic.messages, { topicId: topic.topicId });
});
export const getMessagesOfCurrentConversation = createSelector(
  getNavMode,
  getMessagesFromCurrentTopic,
  getFollowers,
  getGiftTypes,
  (navMode, topicMessages, followers, giftMessages) => {
    const messages = topicMessages || [];

    return messages.map((message) => {
      return {
        _id: uniqueKeyForMessage(message),
        messageId: message.messageId,
        text: message.text,
        image: _isImageFile(message.fileName) ? message.fileName : null,
        video: _isVideoFile(message.fileName) ? message.fileName : null,
        createdAt: new Date(message.updated),
        user: {
          _id: message.userId,
          profile: followers[message.userId],
          // participant: participants[message.userId],
          name: followers[message.userId] ? followers[message.userId].fullName : 'Unknown',
        },
        gift: giftMessages[message.text],
      };
    });
  }
);

// get profiles from followers for all members of the current topic
export const getMembersOfCurrentConversation = createSelector(getCurrentTopic, (currentTopic) => {
  const members = currentTopic?.members || [];
  return members.map((m) => m.profile || m);
});

export const getBroadcastTopic = createSelector(getTopics, (topics) => {
  return Object.keys(topics || {})
    .map((k) => topics[k])
    .find((t) => t.name === 'Broadcast');
});

export const getBroadcastMessages = createSelector(
  getBroadcastTopic,
  getFollowers,
  (broadcastTopic, followers) => {
    const messages = broadcastTopic?.messages || {};
    return Object.keys(messages)
      .reverse()
      .map((k) => {
        return {
          ...messages[k],
          member: followers[messages[k].userId],
        };
      });
  }
);
